import { useState, useEffect } from "react";
import { Label } from "@/modern/components/ui/label";
import { Textarea } from "@/modern/components/ui/textarea";
import { useFileDetailModal } from "../context/FileDetailModalContext";
import { useDebounce } from "@/react/hooks/useDebounce";

interface FileDescriptionSectionProps {
  canEditFile?: boolean;
  isCCImage?: boolean;
}

/**
 * File description section
 */
export const FileDescriptionSection = ({ canEditFile: canEditFileProp, isCCImage: isCCImageProp }: FileDescriptionSectionProps = {}) => {
  // Get file from context
  const { canEditFile: canEditFileFromContext, file } = useFileDetailModal();

  // Use props if provided, otherwise use context values
  const canEditFile = canEditFileProp ?? canEditFileFromContext;
  const isCCImage = isCCImageProp ?? file?.isCCImage ?? false;

  const descriptionPlaceholder = isCCImage ? "Description is provided by CompanyCam" : "Add helpful context for this file";

  // Local state for the description value (for immediate UI updates)
  const [localDescription, setLocalDescription] = useState(file?.description ?? "");

  // Debounce the description value (500ms delay)
  const debouncedDescription = useDebounce(localDescription, 500);

  // Sync local state when file changes
  useEffect(() => {
    setLocalDescription(file?.description ?? "");
  }, [file?.description]);

  // Update record when debounced value changes
  useEffect(() => {
    if (!file?.record || !canEditFile) return;

    const trimmedDescription = debouncedDescription.trim() || null;
    const currentDescription = file.description?.trim() || null;

    // Only update if the value actually changed
    if (trimmedDescription !== currentDescription) {
      file.record.set({ description: trimmedDescription });
    }
  }, [debouncedDescription, file?.record, canEditFile, file?.description]);

  const handleDescriptionChange = (value: string) => {
    setLocalDescription(value);
  };

  return (
    <section className="space-y-2">
      <Label htmlFor="description" className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        Description
      </Label>
      <Textarea
        id="description"
        value={localDescription}
        onChange={(event) => handleDescriptionChange(event.target.value)}
        placeholder={descriptionPlaceholder}
        readOnly={!canEditFile}
        className="min-h-[120px] resize-none"
      />
    </section>
  );
};
